import { useNavigate } from "react-router";
import {
  AppAvatar,
  AppBox,
  AppButton,
  AppDateAndTime,
  AppGridBox,
  AppLink,
  AppPaper,
  AppResultBadge,
  AppScorePill,
  AppText,
  AppTitle,
} from "../../../../commons/components";
import AppBorderBox from "../../../../commons/components/BorderBox";
import { GameResultTypeEnum } from "../../../../commons/enums/match-result-type-enum";
import { IFootballMatchDetail } from "../../../../commons/models/football/interface/i-football-match";
import { IFootballTeam } from "../../../../commons/models/football/interface/i-football-team";
import AppRoundedDivider from "../../../../commons/components/RoundedDivider";
import moment from "moment";
import { useEffect, useState } from "react";

interface MatchInfoProps {
  match: IFootballMatchDetail;
  includeBadge?: boolean;
  currentTeam?: IFootballTeam | null;
}

export default function MatchResultCard({ currentTeam, match, includeBadge }: MatchInfoProps) {
  const navigate = useNavigate();
  const [penaltyGoals, setPenaltyGoals] = useState<{ home?: number; away?: number }>();
  const getResult = (match: { [key: string]: any }): string => {
    const isHomeTeam = currentTeam?.id === match.homeTeam.id;
    const isAwayTeam = currentTeam?.id === match.awayTeam.id;
    const isDraw = penaltyGoals?.home && penaltyGoals.away ? false : match.homeGoals === match.awayGoals;
    if (isDraw) {
      return GameResultTypeEnum.Draw;
    }
    if (
      (isHomeTeam &&
        (match.homeGoals > match.awayGoals ||
          (penaltyGoals?.home && penaltyGoals.away && penaltyGoals?.home > penaltyGoals?.away))) ||
      (isAwayTeam &&
        (match.awayGoals > match.homeGoals ||
          (penaltyGoals?.home && penaltyGoals.away && penaltyGoals?.away > penaltyGoals?.home)))
    ) {
      return GameResultTypeEnum.Win;
    }
    return GameResultTypeEnum.Loss;
  };

  const handleMatchDetailsClick = () => {
    navigate(`/matches/${match.id}`);
    window.scrollTo(0, 0);
  };

  const handleTeamClick = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (match.id && match.penalties.length > 0) {
      const homePenaltyGoal = match.penalties.filter(
        (penalty) => penalty.teamId === match.homeTeam?.id && penalty.goalScored
      ).length;
      const awayPenaltyGoal = match.penalties.filter(
        (penalty) => penalty.teamId === match.awayTeam?.id && penalty.goalScored
      ).length;
      setPenaltyGoals({ home: homePenaltyGoal, away: awayPenaltyGoal });
    }
  }, [match]);

  return (
    <AppPaper className="match-card" shadow="xs" padding="xs">
      <AppBox flexDirection="column" gap="2xs">
        <AppBorderBox border={["Bottom"]} pb="xs">
          <AppGridBox className="match-card-grid" gap="sm" style={{ position: "relative" }}>
            {includeBadge && (
              <AppBox className="status-badge">
                <AppResultBadge status={getResult(match)} />
              </AppBox>
            )}
            <AppGridBox className="align-center left-section" gap="sm">
              <AppBox justifyContent="end">
                <AppLink to={`/teams/${match.homeTeam?.id}`} onClick={handleTeamClick}>
                  <AppBox className="match-home-team" gap="xs" alignItems="center">
                    <AppTitle truncate={true} as="h6" textAlign="right">
                      {match.homeTeam?.shortName}
                    </AppTitle>
                    <AppAvatar username={match.homeTeam?.name || ""} src={match.homeTeam?.logo || ""} />
                  </AppBox>
                </AppLink>
              </AppBox>
              <AppBox justifyContent="center">
                <AppScorePill
                  matchStatus="previous"
                  valueFirst={match.homeGoals || 0}
                  valueSecond={match.awayGoals || 0}
                  penaltyValueFirst={penaltyGoals?.home}
                  penaltyValueSecond={penaltyGoals?.away}
                />
              </AppBox>
              <AppBox justifyContent="start">
                <AppLink to={`/teams/${match.awayTeam?.id}`} onClick={handleTeamClick}>
                  <AppBox className="match-away-team" gap="xs" alignItems="center">
                    <AppAvatar username={match.awayTeam?.name || ""} src={match.awayTeam?.logo || ""} />
                    <AppTitle truncate={true} as="h6">
                      {match.awayTeam?.shortName}
                    </AppTitle>
                  </AppBox>
                </AppLink>
              </AppBox>
            </AppGridBox>
            <AppBox className="match-detail-desktop" gap="xs" justifyContent="end" alignItems="center">
              <AppButton size="lg" variant="light" label="Match Details" onClick={handleMatchDetailsClick}></AppButton>
            </AppBox>
          </AppGridBox>
        </AppBorderBox>
        <AppBox justifyContent="center" alignItems="center">
          <AppDateAndTime date={moment(match.scheduledAt)} format="DD MMM, yyyy" />
          <AppRoundedDivider mx="2xs" />
          <AppDateAndTime date={moment(match.scheduledAt)} format="HH:mm" />
          {match.stadium && (
            <>
              <AppRoundedDivider mx="2xs" />
              <AppText lineClamp={1}>
                {match.stadium.name}, {match.stadium.city}
              </AppText>
            </>
          )}
        </AppBox>
        {/* For mobile view only */}
        <AppBox className="match-detail-mobile" gap="xs" justifyContent="center" alignItems="center">
          <AppButton
            fullWidth
            size="lg"
            variant="light"
            label="Match Details"
            onClick={() => handleMatchDetailsClick()}
          ></AppButton>
        </AppBox>
      </AppBox>
    </AppPaper>
  );
}
